import React, { useContext, useEffect, useState } from 'react';
import { EyeIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import Loading from '../../../components/Loading';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../../context/userContext';
import { useSetupUsergroup } from '../../../hooks/useSetupUsergroup';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import useWarehouseProductItemMovement from '../../../hooks/useWarehouseProductItemMovement';
import { Text } from '../../../components/Text';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'secondItemNo', label: 'Second Item No' },
    { key: 'description', label: 'Description' },
    { key: 'qtyIn', label: 'Quantity In' },
    { key: 'qtyOut', label: 'Quantity Out' },
    { key: 'qtyAdjust', label: 'Quantity Adjust' },
    { key: 'date', label: 'Movement Date' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const ReportStockMovementPage: React.FC = () => {
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    const { currentUser } = useContext(UserContext);
    const { loading, error, movements } = useWarehouseProductItemMovement();
    const { fetchSupplierByUserId, suppliers } = useSetupUsergroup();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');
    const [filterSupplierId, setFilterSupplierId] = useState<string>('');
    const [filterYear, setFilterYear] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterSupplierId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(movements) ? movements : [])
                .map((m) => new Date(m.created_time || 0).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterSupplierId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterSupplierId ? [{
            key: 'supplier',
            label: `Supplier: ${suppliers?.find((s) => s.id?.toString() === filterSupplierId)?.fullname || filterSupplierId}`,
            onClear: () => setFilterSupplierId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Stock Movement Report";
    }, []);

    // Improved Filter logic
    const filteredProductItemMovement = Array.isArray(movements)
        ? movements.filter((movement) => {
            const itemDate = new Date(movement.created_time || new Date());
            const searchLower = searchQuery.toLowerCase();

            // Search across multiple fields
            const matchesSearch =
                (movement.item_no?.toLowerCase().includes(searchLower) ||
                    movement.second_item_no?.toLowerCase().includes(searchLower) ||
                    movement.description?.toLowerCase().includes(searchLower));

            // Date range filter
            const matchesDate =
                (!filterStartDate || itemDate >= new Date(filterStartDate)) &&
                (!filterEndDate || itemDate <= new Date(`${filterEndDate}T23:59:59`)); // Include entire end day

            // Year filter
            const matchesYear =
                !filterYear || itemDate.getFullYear().toString() === filterYear;

            // Supplier filter
            const matchesSupplier =
                !filterSupplierId ||
                movement.supplier_id?.toString() === filterSupplierId;

            return matchesSearch && matchesDate && matchesYear && matchesSupplier;
        })
        : [];

    // Date formatting helper
    const formatDate = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    const generatePDF = () => {
        const today = formatDate(new Date());

        if (!filteredProductItemMovement || filteredProductItemMovement.length === 0) {
            showErrorToast("No data available to generate PDF");
            return;
        }

        // Create landscape PDF
        const doc = new jsPDF('landscape');

        // Title
        doc.setFontSize(14);
        doc.setTextColor(40);
        doc.text(`Stock Movement Report - ${today}`, 14, 15);

        // Report details
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

        if (filterStartDate || filterEndDate) {
            doc.text(`Date range: ${filterStartDate || 'Start'} to ${filterEndDate || 'End'}`, 14, 29);
        }

        if (filterSupplierId) {
            const supplierName = suppliers?.find(s => s.id?.toString() === filterSupplierId)?.fullname || '';
            doc.text(`Supplier: ${supplierName}`, 14, 36);
        }

        // Table data
        const tableData = filteredProductItemMovement.map((item, index) => [
            index + 1,
            item.item_no || 'N/A',
            item.second_item_no || 'N/A',
            item.description || 'N/A',
            item.quantity_in || 0,
            item.quantity_out || 0,
            item.quantity_adjust || 0,
            dateFormat(item.created_time?.toString(), "mmm dd, yyyy hh:mm:ss")
        ]);

        // AutoTable configuration
        doc.autoTable({
            startY: 40,
            head: [
                [
                    'S/N',
                    'Item No',
                    'Second Item No',
                    'Description',
                    'Qty In',
                    'Qty Out',
                    'Qty Adjust',
                    'Movement Date'
                ]
            ],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: [51, 102, 153], // Dark blue header
                textColor: 255,
                fontSize: 9
            },
            styles: {
                fontSize: 8,
                cellPadding: 3,
                overflow: 'linebreak'
            },
            columnStyles: {
                0: { cellWidth: 10 }, // S/N
                1: { cellWidth: 25 }, // Item No
                2: { cellWidth: 25 }, // Second Item No
                3: { cellWidth: 40 }, // Description
                4: { cellWidth: 15 }, // Qty In
                5: { cellWidth: 15 }, // Qty Out
                6: { cellWidth: 15 }, // Qty Adjust
                7: { cellWidth: 20 }  // Movement Date
            },
            margin: { left: 10 }
        });

        doc.save(`stock_movement_report_${today}.pdf`);
    };

    // CSV Generation
    const generateCSV = () => {
        if (!filteredProductItemMovement) {
            showErrorToast("CSV generation failed. Product item is not available.");
            return;
        }
        const csvData = [
            ['S/N', 'Item No', 'Second Item No', 'Description', 'Quantity In', 'Quantity Out', 'Quantity Adjust'],
            ...filteredProductItemMovement.map((item, index) => [
                index + 1,
                item.item_no,
                item.second_item_no,
                item.description,
                item.quantity_in || '0',
                item.quantity_out || '0',
                item.quantity_adjust || '0'
            ])
        ];

        const csvContent = 'data:text/csv;charset=utf-8,' + csvData.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        const today = formatDate(new Date());
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `stock_movement_report_${today}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Excel Generation
    const generateExcel = async () => {
        if (!filteredProductItemMovement) {
            showErrorToast("Excel generation failed. Product item is not available.");
            return;
        }

        const data = filteredProductItemMovement.map((item, index) => ({
            SNo: index + 1,
            ItemNo: item.item_no,
            SecondItemNo: item.second_item_no,
            Description: item.description,
            QuantityIn: item.quantity_in || "0",
            QuantityOut: item.quantity_out || "0",
            QuantityAdjust: item.quantity_adjust || "0"
        }));

        if (data.length === 0) {
            showErrorToast("Excel generation failed. No data available.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Stock Movement");
        sheet.columns = Object.keys(data[0]).map((key) => ({ header: key, key, width: 18 }));
        sheet.addRows(data);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `stock_movement_report_${formatDate(new Date())}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Stock Movement</Heading>
                </div>
                <Dropdown>
                    <DropdownButton aria-label="More options">
                        Print Report
                    </DropdownButton>
                    <DropdownMenu anchor="bottom end">
                        <DropdownItem onClick={generatePDF}>PDF</DropdownItem>
                        <DropdownItem onClick={generateCSV}>CSV</DropdownItem>
                        <DropdownItem onClick={generateExcel}>Excel</DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search stock movement…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Item No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'secondItemNo') && <TableHeader>Second Item No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'description') && <TableHeader>Description</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyIn') && <TableHeader>Quantity In</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyOut') && <TableHeader>Quantity Out</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyAdjust') && <TableHeader>Quantity Adjust</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'date') && <TableHeader>Movement Date</TableHeader>}
                                    <TableHeader>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredProductItemMovement.length > 0) ? [...filteredProductItemMovement].sort((a, b) => {
                                    const diff = new Date(a.created_time || 0).getTime() - new Date(b.created_time || 0).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((item, index) => (
                                    <TableRow key={index} title={`${index}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell className="text-zinc-500">{item.item_no || '-'}</TableCell>
                                        {isColumnVisible(columnVisibility, 'secondItemNo') && <TableCell className="text-zinc-500">{item.second_item_no || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'description') && <TableCell className="text-zinc-500">{item.description || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyIn') && <TableCell className="text-zinc-400">{item.quantity_in || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyOut') && <TableCell className="text-zinc-400">{item.quantity_out || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyAdjust') && <TableCell className="text-zinc-400">{item.quantity_adjust || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'date') && <TableCell className="text-zinc-400">{dateFormat(item.created_time?.toString(), "mmm dd, yyyy hh:mm:ss")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4">
                                            <a className="text-zinc-600 hover:text-zinc-900" onClick={() => navigate(`/gp/reports/stockmovement/view/${item.id}`)}>
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock movement found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() !== "5") && (
                    <div>
                        <Text>By Supplier</Text>
                        <Select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)}>
                            <option value="">All Suppliers</option>
                            {suppliers?.map((supplier) => (
                                <option key={supplier.id} value={supplier.id?.toString()}>
                                    {supplier.fullname}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>
        </>
    );
};

export default ReportStockMovementPage;
